import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Typography, List, Tag, Space, Empty, Alert, Select, Spin } from 'antd';
import { CalendarOutlined, ReloadOutlined, LinkOutlined, SyncOutlined } from '@ant-design/icons';
import { fetchAuthSession } from '../auth/gateway';
import { secureLogger } from '../utils/secureLogger';

const { Title, Text, Paragraph } = Typography;

interface CalendarEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  isAllDay: boolean;
  location?: string;
  status?: string;
  htmlLink?: string;
  organizer?: string;
}

const decodeJwtPayload = (token: string) => {
  const base64Url = token.split('.')[1];
  if (!base64Url) throw new Error('Invalid JWT format');
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) base64 += '=';
  const decoded = atob(base64);
  const jsonPayload = decodeURIComponent(
    decoded
      .split('')
      .map((char) => `%${('00' + char.charCodeAt(0).toString(16)).slice(-2)}`)
      .join('')
  );
  return JSON.parse(jsonPayload);
};

const extractGoogleAccessToken = (payload: Record<string, any>): string | null => {
  const identities = payload?.identities;
  if (Array.isArray(identities)) {
    const googleIdentity = identities.find((identity) => 
      identity.providerName?.toLowerCase().includes('google')
    );
    if (googleIdentity?.access_token) {
      return googleIdentity.access_token as string;
    }
  }
  if (payload['custom:google_access_token']) {
    return payload['custom:google_access_token'];
  }
  return null;
};

const formatEventTime = (value: string, isAllDay: boolean) => {
  if (!value) return '未定';
  if (isAllDay) {
    return `${value} (終日)`;
  }
  try {
    const date = new Date(value);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return value;
  }
};

const CalendarPage: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [rangeDays, setRangeDays] = useState(7);

  const fetchCalendarEvents = useCallback(async (token: string, days = rangeDays) => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const timeMin = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const timeMax = new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
      const params = new URLSearchParams({
        singleEvents: 'true',
        orderBy: 'startTime',
        maxResults: '50',
        timeMin,
        timeMax,
      });

      const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to fetch calendar events');
      }

      const data = await response.json();
      const mappedEvents: CalendarEvent[] = (data.items || []).map((item: any) => ({
        id: item.id,
        summary: item.summary || '（タイトルなし）',
        start: item.start?.dateTime || item.start?.date || '',
        end: item.end?.dateTime || item.end?.date || '',
        isAllDay: Boolean(item.start?.date && !item.start?.dateTime),
        location: item.location,
        status: item.status,
        htmlLink: item.htmlLink,
        organizer: item.organizer?.displayName || item.organizer?.email,
      }));

      setEvents(mappedEvents);
      setLastSynced(new Date());
      secureLogger.info('Google Calendar events synced', { count: mappedEvents.length });
    } catch (err) {
      secureLogger.error('Failed to load Google Calendar events', { error: (err as Error).message });
      setError('Googleカレンダーの取得に失敗しました。再度お試しください。');
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  const syncFromSession = useCallback(async (overrideRange?: number) => {
    try {
      setError(null);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) {
        throw new Error('Cognito IDトークンを取得できませんでした。');
      }
      const payload = decodeJwtPayload(idToken);
      const googleToken = extractGoogleAccessToken(payload);
      if (!googleToken) {
        throw new Error('Googleアクセストークンが含まれていません。Cognitoのスコープ設定を確認してください。');
      }
      setAccessToken(googleToken);
      await fetchCalendarEvents(googleToken, overrideRange ?? rangeDays);
    } catch (err) {
      const message = (err as Error).message || 'Google認証に失敗しました。';
      setError(message);
      secureLogger.error('Failed to obtain Google token for Calendar', { error: message });
    }
  }, [fetchCalendarEvents, rangeDays]);

  useEffect(() => {
    syncFromSession();
  }, [syncFromSession]);

  const handleRefresh = () => {
    if (accessToken) {
      fetchCalendarEvents(accessToken);
    } else {
      syncFromSession();
    }
  };

  const handleRangeChange = (value: number) => {
    setRangeDays(value);
    if (accessToken) {
      fetchCalendarEvents(accessToken, value);
    } else {
      syncFromSession(value);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space align="start">
            <CalendarOutlined style={{ fontSize: 32, color: '#1677ff' }} />
            <div>
              <Title level={4} style={{ marginBottom: 4 }}>Googleカレンダー連携</Title>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Thinkletで取得した行動データと Google カレンダー上の予定を突き合わせ、日々の活動を記録します。
              </Paragraph>
            </div>
          </Space>
          <Space wrap>
            <Button type="primary" onClick={handleRefresh} icon={<SyncOutlined />} disabled={loading}>
              最新の予定を取得
            </Button>
            <Button 
              onClick={() => accessToken && fetchCalendarEvents(accessToken, rangeDays)} 
              icon={<ReloadOutlined />} 
              disabled={!accessToken || loading}
            >
              予定のみ再読み込み
            </Button>
            <Select
              value={rangeDays}
              onChange={handleRangeChange}
              style={{ width: 160 }}
              options={[
                { value: 3, label: '直近3日' },
                { value: 7, label: '直近1週間' },
                { value: 14, label: '直近2週間' },
                { value: 30, label: '直近1か月' },
              ]}
            />
            {lastSynced && (
              <Text type="secondary">
                最終更新: {lastSynced.toLocaleString()}
              </Text>
            )}
          </Space>
          {error && (
            <Alert type="error" showIcon message={error} />
          )}
        </Space>
      </Card>

      <Card title="予定一覧" extra={<Text type="secondary">{events.length} 件</Text>}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>カレンダーを同期中...</div>
          </div>
        ) : events.length === 0 ? (
          <Empty description="表示する予定がありません" />
        ) : (
          <List
            dataSource={events}
            renderItem={(event) => (
              <List.Item key={event.id}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space align="center" wrap>
                    <Text strong>{event.summary}</Text>
                    {event.status && <Tag color="blue">{event.status}</Tag>}
                    {event.isAllDay && <Tag color="gold">終日</Tag>}
                  </Space>
                  <Text>
                    {formatEventTime(event.start, event.isAllDay)} 〜 {formatEventTime(event.end, event.isAllDay)}
                  </Text>
                  {event.location && <Text type="secondary">📍 {event.location}</Text>}
                  {event.organizer && <Text type="secondary">主催: {event.organizer}</Text>}
                  {event.htmlLink && (
                    <Button
                      type="link"
                      icon={<LinkOutlined />}
                      href={event.htmlLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ paddingLeft: 0 }}
                    >
                      Googleカレンダーで開く
                    </Button>
                  )}
                </Space>
              </List.Item>
            )}
          />
        )}
      </Card>
    </Space>
  );
};

export default CalendarPage;
